import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CompanyData } from "@/contexts/GameContext";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";

interface Props {
  company: CompanyData;
  isOwner: boolean;
}

export default function StockPanel({ company, isOwner }: Props) {
  const { isDark } = useTheme();
  const { goPublic, getStockMarket, updateCompany } = useGame();
  const { user, spendCoins, buyStock } = useAuth();
  const navigate = useNavigate();

  const [ipoShares, setIpoShares] = useState<string>("300000");
  const [ipoPrice, setIpoPrice] = useState<string>("100");
  const [buybackShares, setBuybackShares] = useState<string>("1000");
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const [loading, setLoading] = useState(false);

  const market = getStockMarket(company.id);

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const inputBg = isDark ? "bg-white/5 border-white/10 text-white placeholder-gray-500" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400";
  const divider = isDark ? "border-white/5" : "border-gray-100";

  const ipoSharesNum = parseInt(ipoShares) || 0;
  const ipoPriceNum = parseFloat(ipoPrice) || 0;
  const ipoCost = Math.floor(ipoPriceNum * ipoSharesNum * 0.01);
  const buybackSharesNum = parseInt(buybackShares) || 0;
  const buybackCost = buybackSharesNum * company.stockPrice;

  const handleIPO = async () => {
    if (!user || !isOwner) return;
    if (ipoSharesNum <= 0 || ipoPriceNum <= 0) {
      setMessage({ text: "株数と株価を正しく入力してください", ok: false });
      return;
    }
    if (ipoSharesNum > company.totalShares) {
      setMessage({ text: `公開株数は発行済株式数(${company.totalShares.toLocaleString()})以下にしてください`, ok: false });
      return;
    }
    setLoading(true);
    const ok = goPublic(company.id, ipoSharesNum, ipoPriceNum, spendCoins);
    if (ok) {
      setMessage({ text: `IPO成功！${ipoSharesNum.toLocaleString()}株を ${ipoPriceNum.toLocaleString()} coins/株で公開しました`, ok: true });
    } else {
      setMessage({ text: `IPO手数料が不足しています（必要: ${ipoCost.toLocaleString()} coins）`, ok: false });
    }
    setLoading(false);
  };

  const handleBuyback = () => {
    if (!user || !isOwner) return;
    if (buybackSharesNum <= 0) {
      setMessage({ text: "株数を入力してください", ok: false });
      return;
    }
    const paid = spendCoins(buybackCost);
    if (!paid) {
      setMessage({ text: `コインが不足しています（必要: ${buybackCost.toLocaleString()} coins）`, ok: false });
      return;
    }
    // 自社株買い: 公開株数を減らす
    updateCompany(company.id, {
      publicShares: Math.max(0, company.publicShares - buybackSharesNum),
    });
    buyStock(company.id, buybackSharesNum, company.stockPrice);
    setMessage({ text: `自社株買い完了！${buybackSharesNum.toLocaleString()}株を取得しました`, ok: true });
  };

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <div className={`rounded-2xl border p-5 ${cardBg}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-base font-bold ${textPrimary}`}>株式情報</h3>
          {company.isPublic && (
            <button
              onClick={() => navigate("/stock-market")}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl bg-amber-400/10 text-amber-400 hover:bg-amber-400/20 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-stock-line"></i>
              株式市場で見る
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className={`rounded-xl p-3 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1`}>上場状況</p>
            <div className={`flex items-center gap-1.5`}>
              <div className={`w-2 h-2 rounded-full ${company.isPublic ? "bg-emerald-400" : "bg-gray-400"}`}></div>
              <p className={`text-sm font-bold ${company.isPublic ? "text-emerald-400" : textSecondary}`}>
                {company.isPublic ? "上場中" : "未上場"}
              </p>
            </div>
          </div>
          <div className={`rounded-xl p-3 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1`}>現在株価</p>
            <p className={`text-sm font-black ${textPrimary}`}>{company.stockPrice.toLocaleString()}</p>
            <p className={`text-xs ${textSecondary}`}>coins/株</p>
          </div>
          <div className={`rounded-xl p-3 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1`}>発行済株式</p>
            <p className={`text-sm font-black ${textPrimary}`}>{(company.totalShares / 10000).toFixed(0)}万株</p>
          </div>
          <div className={`rounded-xl p-3 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1`}>時価総額</p>
            <p className={`text-sm font-black ${textPrimary}`}>
              {(company.stockPrice * company.totalShares / 1000000).toFixed(2)}M
            </p>
            <p className={`text-xs ${textSecondary}`}>coins</p>
          </div>
        </div>

        {/* Shareholders summary */}
        {company.isPublic && market && (
          <div className={`mt-4 pt-4 border-t ${divider}`}>
            <p className={`text-xs font-semibold mb-3 ${textSecondary}`}>主要株主</p>
            <div className="space-y-2">
              {Object.values(market.shareholders)
                .sort((a, b) => b.shares - a.shares)
                .slice(0, 5)
                .map((sh) => {
                  const pct = (sh.shares / company.totalShares) * 100;
                  return (
                    <div key={sh.userId} className="flex items-center gap-3">
                      <span className={`text-xs w-28 truncate ${textPrimary}`}>{sh.userName}</span>
                      <div className={`flex-1 h-1.5 rounded-full ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                        <div
                          className="h-1.5 rounded-full bg-amber-400"
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        ></div>
                      </div>
                      <span className={`text-xs w-12 text-right ${textSecondary}`}>{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* IPO Section (オーナーのみ・未上場時) */}
      {isOwner && !company.isPublic && (
        <div className={`rounded-2xl border p-5 ${cardBg}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-400/10">
              <i className="ri-stock-line text-xl text-amber-400"></i>
            </div>
            <div>
              <h3 className={`text-base font-bold ${textPrimary}`}>IPO（新規株式公開）</h3>
              <p className={`text-xs ${textSecondary}`}>株式を公開して他プレイヤーが投資できるようにする</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={`text-xs font-semibold mb-2 block ${textSecondary}`}>
                  公開株数（最大: {company.totalShares.toLocaleString()}株）
                </label>
                <input
                  type="number"
                  value={ipoShares}
                  onChange={(e) => setIpoShares(e.target.value)}
                  className={`w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/30 ${inputBg}`}
                  min="1"
                  max={company.totalShares}
                />
                <p className={`text-xs mt-1 ${textSecondary}`}>
                  全体の {ipoSharesNum > 0 ? ((ipoSharesNum / company.totalShares) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div>
                <label className={`text-xs font-semibold mb-2 block ${textSecondary}`}>公開価格 (coins/株)</label>
                <input
                  type="number"
                  value={ipoPrice}
                  onChange={(e) => setIpoPrice(e.target.value)}
                  className={`w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/30 ${inputBg}`}
                  min="1"
                />
                <p className={`text-xs mt-1 ${textSecondary}`}>
                  時価総額: {(ipoPriceNum * company.totalShares).toLocaleString()} coins
                </p>
              </div>
            </div>

            {/* IPO Cost Summary */}
            <div className={`rounded-xl p-4 ${isDark ? "bg-amber-400/5 border border-amber-400/20" : "bg-amber-50 border border-amber-200"}`}>
              <div className="flex justify-between text-sm mb-2">
                <span className={textSecondary}>IPO手数料（公開株数 × 株価 × 1%）</span>
                <span className="text-amber-400 font-bold">{ipoCost.toLocaleString()} coins</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className={textSecondary}>あなたの残高</span>
                <span className={`font-bold ${(user?.coins ?? 0) >= ipoCost ? "text-emerald-400" : "text-red-400"}`}>
                  {(user?.coins ?? 0).toLocaleString()} coins
                </span>
              </div>
            </div>

            {message && (
              <div className={`rounded-xl px-4 py-3 text-sm font-medium ${message.ok ? "bg-emerald-400/10 text-emerald-400" : "bg-red-400/10 text-red-400"}`}>
                <i className={`${message.ok ? "ri-check-line" : "ri-error-warning-line"} mr-2`}></i>
                {message.text}
              </div>
            )}

            <button
              onClick={handleIPO}
              disabled={loading || ipoSharesNum <= 0 || ipoPriceNum <= 0}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-black font-black text-sm transition-all cursor-pointer whitespace-nowrap hover:from-amber-300 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "処理中..." : "IPOを実行する"}
            </button>
          </div>
        </div>
      )}

      {/* Buyback Section (オーナーのみ・上場時) */}
      {isOwner && company.isPublic && (
        <div className={`rounded-2xl border p-5 ${cardBg}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-400/10">
              <i className="ri-arrow-go-back-line text-xl text-emerald-400"></i>
            </div>
            <div>
              <h3 className={`text-base font-bold ${textPrimary}`}>自社株買い</h3>
              <p className={`text-xs ${textSecondary}`}>市場から自社株を買い戻して流通株数を減らす</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className={`text-xs font-semibold mb-2 block ${textSecondary}`}>
                買い戻し株数（流通株: {company.publicShares.toLocaleString()}株）
              </label>
              <input
                type="number"
                value={buybackShares}
                onChange={(e) => setBuybackShares(e.target.value)}
                className={`w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/30 ${inputBg}`}
                min="1"
                max={company.publicShares}
              />
            </div>

            <div className={`rounded-xl p-3 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
              <div className="flex justify-between text-sm">
                <span className={textSecondary}>買い戻しコスト</span>
                <span className={`font-bold ${(user?.coins ?? 0) >= buybackCost ? "text-emerald-400" : "text-red-400"}`}>
                  {buybackCost.toLocaleString()} coins
                </span>
              </div>
            </div>

            {message && (
              <div className={`rounded-xl px-4 py-3 text-sm font-medium ${message.ok ? "bg-emerald-400/10 text-emerald-400" : "bg-red-400/10 text-red-400"}`}>
                <i className={`${message.ok ? "ri-check-line" : "ri-error-warning-line"} mr-2`}></i>
                {message.text}
              </div>
            )}

            <button
              onClick={handleBuyback}
              disabled={buybackSharesNum <= 0 || buybackSharesNum > company.publicShares}
              className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-black text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              自社株買いを実行
            </button>
          </div>
        </div>
      )}

      {/* Non-owner: Go to market */}
      {!isOwner && company.isPublic && (
        <div className={`rounded-2xl border p-5 text-center ${cardBg}`}>
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-amber-400/10 mx-auto mb-3">
            <i className="ri-stock-line text-2xl text-amber-400"></i>
          </div>
          <p className={`text-sm font-bold mb-1 ${textPrimary}`}>この会社の株式を取引する</p>
          <p className={`text-xs mb-4 ${textSecondary}`}>株式市場でリアルタイムに売買できます</p>
          <button
            onClick={() => navigate("/stock-market")}
            className="bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold rounded-full px-6 py-2.5 cursor-pointer whitespace-nowrap text-sm hover:from-amber-300 hover:to-orange-400 transition-all"
          >
            株式市場へ
          </button>
        </div>
      )}
    </div>
  );
}
